import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { profilesTrackingService } from "../services";
import { EditProfileModal } from "../components/edit-profile-modal";
import { Sidebar } from "../../../components/sidebar";
import { toast } from "react-toastify";
import { useEditProfileModal } from "../../../contexts/useEditProfileModal";
import {
  Container,
  MainContent,
  ContentWrapper,
  Header,
  HeaderTitle,
  HeaderActions,
  SearchBar,
  FilterButton,
  AddButton,
  ProfilesGrid,
  ProfileCard,
  ProfileHeader,
  ProfileAvatar,
  ProfileStatus,
  ProfileInfo,
  ProfileName,
  ProfileCategory,
  ProfileStats,
  StatItem,
  StatValue,
  StatLabel,
  ProfileActions,
  ActionButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingContainer,
  LoadingSpinner,
} from "./styles";

interface Profile {
  id: string;
  name: string;
  category: string;
  status: "active" | "pending" | "paused" | "preparing";
  avatar?: string;
  stats: {
    views: number;
    clicks: number;
    ranking: number;
  };
  // Campos adicionais opcionais
  shortName?: string;
  openingDate?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  description?: string;
  hours?: {
    monday?: string | string[];
    tuesday?: string | string[];
    wednesday?: string | string[];
    thursday?: string | string[];
    friday?: string | string[];
    saturday?: string | string[];
    sunday?: string | string[];
  };
}

export const ProfilesTracking: React.FC = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const { isOpen: isModalOpen, openModal, closeModal: closeModalContext } = useEditProfileModal();

  const setIsModalOpen = (open: boolean) => {
    if (open) {
      openModal();
    } else {
      closeModalContext();
    }
  };

  /**
   * Carrega todos os perfis do usuário na inicialização do componente.
   */
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        console.log("teste")
        const data = await profilesTrackingService.listProfiles();
        // Garantir que sempre tenhamos um array
        const profilesArray = Array.isArray(data) ? data : [];
        setProfiles(profilesArray);
      } catch (error) {
        console.error("Erro ao carregar perfis:", error);
        setProfiles([]); // Definir array vazio em caso de erro
        toast.error("Erro ao carregar perfis");
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  /**
   * Abre o modal de edição para o perfil selecionado.
   * 
   * @param profile - Dados do perfil a editar
   */
  const handleEditProfile = (profile: Profile) => {
    setEditingProfile(profile);
    setIsModalOpen(true);
  };

  /**
   * Salva as alterações do perfil e atualiza a lista local.
   * 
   * @param updatedProfile - Dados atualizados do perfil
   */
  const handleSaveProfile = async (updatedProfile: Profile) => {
    console.log('handleSaveProfile called with:', updatedProfile);
    try {
      console.log('ABOUT TO CALL updateProfile with profileId:', updatedProfile.id);
      await profilesTrackingService.updateProfile(updatedProfile.id, updatedProfile);
      console.log('updateProfile completed');
      
      // Atualizar a lista de perfis
      setProfiles((prev) =>
        prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p))
      );
      
      toast.success("Perfil atualizado com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      toast.error("Erro ao atualizar perfil");
      throw error;
    }
  };

  /**
   * Fecha o modal de edição e limpa o perfil sendo editado.
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProfile(null);
  };

  /**
   * Retorna a cor da badge de status para exibição visual.
   * 
   * @param status - Status do perfil
   * @returns Código de cor em formato hexadecimal
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "#1E8E3E";
      case "pending":
        return "#F9AB00";
      case "paused":
        return "#D93025";
      case "preparing":
        return "#1264C7";
      default:
        return "#5F6368";
    }
  };

  /**
   * Converte o código de status para texto legível em português.
   * 
   * @param status - Código de status do perfil
   * @returns Texto do status formatado
   */
  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "Ativo";
      case "pending":
        return "Pendente";
      case "paused":
        return "Pausado";
      case "preparing":
        return "Em Preparação";
      default:
        return status;
    }
  };

  const filteredProfiles = Array.isArray(profiles)
    ? profiles.filter((profile) =>
        profile.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  if (loading) {
    return (
      <Container>
        <MainContent>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Carregando perfis...</p>
          </LoadingContainer>
        </MainContent>
      </Container>
    );
  }

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <ContentWrapper>
          <Header>
            <div>
              <HeaderTitle>Perfis em Acompanhamento</HeaderTitle>
              <p style={{ color: "#5F6368", margin: "8px 0 0 0" }}>
                Gerencie e monitore todos os seus perfis
              </p>
            </div>
            <HeaderActions>
              <SearchBar
                type="text"
                placeholder="Buscar perfis..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              />
              <FilterButton>
                <span className="material-icons">filter_list</span>
                Filtros
              </FilterButton>
              <AddButton onClick={() => navigate("/georanking")}>
                <span className="material-icons">add</span>
                Novo Perfil
              </AddButton>
            </HeaderActions>
          </Header>

          {filteredProfiles.length > 0 ? (
            <ProfilesGrid>
              {filteredProfiles.map((profile) => (
                <ProfileCard key={profile.id}>
                  <ProfileHeader>
                    <ProfileAvatar>
                      {profile.name.substring(0, 2).toUpperCase()}
                    </ProfileAvatar>
                    <ProfileStatus $color={getStatusColor(profile.status)}>
                      {getStatusText(profile.status)}
                    </ProfileStatus>
                  </ProfileHeader>

                  <ProfileInfo>
                    <ProfileName>{profile.name}</ProfileName>
                    <ProfileCategory>{profile.category}</ProfileCategory>
                  </ProfileInfo>

                  <ProfileStats>
                    <StatItem>
                      <StatValue>{profile.stats.views}</StatValue>
                      <StatLabel>Visualizações</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{profile.stats.clicks}</StatValue>
                      <StatLabel>Cliques</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>#{profile.stats.ranking}</StatValue>
                      <StatLabel>Ranking</StatLabel>
                    </StatItem>
                  </ProfileStats>

                  <ProfileActions>
                    <ActionButton onClick={() => navigate(`/dashboard/${profile.id}`)}>
                      <span className="material-icons">visibility</span>
                      Ver Detalhes
                    </ActionButton>
                    <ActionButton $secondary onClick={() => handleEditProfile(profile)}>
                      <span className="material-icons">edit</span>
                    </ActionButton>
                  </ProfileActions>
                </ProfileCard>
              ))}
            </ProfilesGrid>
          ) : (
            <EmptyState>
              <EmptyStateIcon className="material-icons">
                inbox
              </EmptyStateIcon>
              <EmptyStateTitle>Nenhum perfil encontrado</EmptyStateTitle>
              <EmptyStateText>
                {searchQuery
                  ? "Tente ajustar sua busca"
                  : "Comece adicionando seu primeiro perfil"}
              </EmptyStateText>
              {!searchQuery && (
                <AddButton onClick={() => navigate("/georanking")} style={{ marginTop: "24px" }}>
                  <span className="material-icons">add</span>
                  Adicionar Perfil
                </AddButton>
              )}
            </EmptyState>
          )}
        </ContentWrapper>
      </MainContent>

      <EditProfileModal
        isOpen={isModalOpen}
        profile={editingProfile}
        onClose={handleCloseModal}
        onSave={handleSaveProfile}
      />
    </Container>
  );
};
